import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Loader2, Download, Smartphone, Shield, Key } from "lucide-react";
import GoldShieldLogo from "@/components/GoldShieldLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { adminLogin, verify2FALogin, getStoredToken } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function AdminLoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  
  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [twoFACode, setTwoFACode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [verifying2FA, setVerifying2FA] = useState(false);
  
  const { login, admin, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is already authenticated
  useEffect(() => {
    const verifyExistingSession = async () => {
      const token = getStoredToken();
      if (token) {
        try {
          await checkAuth();
          if (admin) {
            navigate("/admin", { replace: true });
            return;
          }
        } catch (e) {
          // Token invalid, stay on login
        }
      }
      setCheckingAuth(false);
    };
    verifyExistingSession();
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (!checkingAuth && admin) {
      navigate("/admin", { replace: true });
    }
  }, [admin, checkingAuth, navigate]);

  // PWA Install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Handle PWA Install
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      alert("Pentru a instala pe iOS:\n1. Apasă butonul Share (⬆️)\n2. Alege 'Add to Home Screen'\n\nPentru desktop Chrome:\n1. Click pe iconița ⊕ din bara de adrese");
      return;
    }
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstallBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsLoading(true);

    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();

    if (!trimmedUsername || !trimmedPassword) {
      setError("Please enter username and password.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await adminLogin({ 
        username: trimmedUsername, 
        password: trimmedPassword 
      });
      
      // Check if 2FA is required
      if (response?.requires_2fa) {
        setRequires2FA(true);
        setTempToken(response.temp_token);
        setIsLoading(false);
        return;
      }
      
      // No 2FA - complete login
      if (response?.access_token) {
        const adminData = {
          username: response.username,
          role: response.role || "admin",
          must_change_password: response.must_change_password
        };
        login(adminData, response.must_change_password);
        navigate(location.state?.from || "/admin");
      } else {
        setError("Invalid login response.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (event) => {
    event.preventDefault();
    setError("");
    
    const code = twoFACode.trim().toUpperCase();
    if (!code) {
      setError("Please enter the verification code.");
      return;
    }

    setVerifying2FA(true);

    try {
      const response = await verify2FALogin({
        code,
        temp_token: tempToken,
        use_backup: useBackupCode
      });
      
      if (response?.access_token) {
        const adminData = {
          username: response.username,
          role: response.role || "admin",
          must_change_password: response.must_change_password
        };
        login(adminData, response.must_change_password);
        navigate(location.state?.from || "/admin");
      } else {
        setError("Invalid verification response.");
      }
    } catch (err) {
      console.error("2FA verification error:", err);
      setError(err.response?.data?.detail || "Invalid code. Please try again.");
    } finally {
      setVerifying2FA(false);
    }
  };

  const resetLogin = () => {
    setRequires2FA(false);
    setTempToken("");
    setTwoFACode("");
    setUseBackupCode(false);
    setError("");
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <GoldShieldLogo className="w-16 h-20" />
          </div>
          <Loader2 className="w-6 h-6 text-[#22c55e] animate-spin mx-auto mb-2" />
          <p className="text-slate-400 text-sm">Se verifică sesiunea...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="flex justify-center mb-4">
            <GoldShieldLogo className="w-16 h-20" />
          </div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white">
            RTS EXPRESS
          </h1>
          <p className="text-slate-400 text-sm mt-1">Admin Panel</p>
        </div>

        <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-8">
          {/* 2FA Verification Step */}
          {requires2FA ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-[#22c55e]" />
                <h2 className="text-xl font-bold text-white">Two-Factor Authentication</h2>
              </div>
              
              <p className="text-slate-400 text-sm mb-4">
                {useBackupCode 
                  ? "Enter one of your backup codes to sign in."
                  : "Enter the 6-digit code from your authenticator app."}
              </p>

              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handle2FASubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">
                    {useBackupCode ? "Backup Code" : "Verification Code"}
                  </label>
                  <Input
                    data-testid="2fa-code-input"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, useBackupCode ? 8 : 6))}
                    placeholder={useBackupCode ? "XXXXXXXX" : "000000"}
                    className="bg-slate-900 border-slate-700 text-white text-center text-2xl font-mono tracking-widest"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="one-time-code"
                    spellCheck="false"
                    maxLength={useBackupCode ? 8 : 6}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="2fa-submit"
                  className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-black font-semibold"
                  disabled={verifying2FA}
                >
                  {verifying2FA ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
                </Button>
              </form>

              <div className="mt-4 pt-4 border-t border-slate-800 space-y-2">
                <button
                  type="button"
                  onClick={() => setUseBackupCode(!useBackupCode)}
                  className="w-full text-sm text-slate-400 hover:text-white flex items-center justify-center gap-2"
                >
                  <Key className="w-4 h-4" />
                  {useBackupCode ? "Use authenticator app instead" : "Use a backup code"}
                </button>
                <button
                  type="button"
                  onClick={resetLogin}
                  className="w-full text-sm text-slate-500 hover:text-slate-300"
                >
                  ← Back to login
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Normal Login */}
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-5 h-5 text-[#22c55e]" />
                <h2 className="text-xl font-bold text-white">Admin Login</h2>
              </div>

              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Username</label>
                  <Input
                    data-testid="admin-username-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="admin"
                    className="bg-slate-900 border-slate-700 text-white"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="username"
                    spellCheck="false"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Password</label>
                  <Input
                    data-testid="admin-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-slate-900 border-slate-700 text-white"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="current-password"
                    spellCheck="false"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="admin-login-submit"
                  className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-black font-semibold"
                  disabled={isLoading}
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
                </Button>
              </form>
            </>
          )}
        </div>

        {/* PWA Install Banner */}
        {showInstallBanner && !requires2FA && (
          <div className="mt-4 bg-slate-800/50 border border-slate-700 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#22c55e]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Download className="w-5 h-5 text-[#22c55e]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">Instalează RTS Admin</p>
                <p className="text-slate-400 text-xs">Accesează rapid de pe Home Screen</p>
              </div>
              <Button
                onClick={handleInstallClick}
                size="sm"
                className="bg-[#22c55e] hover:bg-[#16a34a] text-black text-xs"
                data-testid="pwa-install-btn"
              >
                Instalează
              </Button>
            </div>
          </div>
        )}

        {/* Manual Install Instructions */}
        {!requires2FA && (
          <div className="mt-4 text-center">
            <button
              onClick={handleInstallClick}
              className="text-slate-500 hover:text-slate-300 text-xs inline-flex items-center gap-1 transition-colors"
            >
              <Smartphone className="w-3 h-3" />
              Instalează ca aplicație
            </button>
          </div>
        )}

        <p className="text-center text-slate-600 text-xs mt-4">
          RTS EXPRESS LTD © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
